/**
 * 파일명 : evl0110m.tsx
 * 설명 : 마감일 관리 화면 - 모바일
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2026-01-14     이예원    최초작성
 * 1.1       2026-02-02     조동현    모바일 버전 생성
 ***********************************************/

/************************************************
 * Import 영역
 ************************************************/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Button, Tag, Modal, DatePicker, Typography, message, Select, Empty, Tabs, Input, Switch } from "antd";
import { isMobile } from "react-device-detect";
import dayjs from "dayjs";

/* Reducer */
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { Evl0110mAction } from "../../../redux/slice/evl/evl0110m_slice";

/* Models */
import { IEvlMemberListState, EvaluationStatus, TabType } from "../../../redux/models/evl/evl0110m_model";

import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

const { Text } = Typography;

const MobileEvl0110m: React.FC = () => {
  const dispatch = useAppDispatch();
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [isViewInitialized, setIsViewInitialized] = useState<boolean>(false);

  // 화면 크기 감지
  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Local state for modal
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<IEvlMemberListState | null>(null);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const confirmModalRefs = useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  // Redux state
  const selectedEvalType = useAppSelector(
    (state) => state.Evl0110m.selectedEvalType,
  );
  const selectedStatus = useAppSelector(
    (state) => state.Evl0110m.selectedStatus,
  );
  const selectedTeam = useAppSelector((state) => state.Evl0110m.selectedTeam);
  const searchName = useAppSelector((state) => state.Evl0110m.searchName);
  const pageSize = useAppSelector((state) => state.Evl0110m.pageSize);
  const allEmployees = useAppSelector((state) => state.Evl0110m.allEmployees);
  const rawBackendData = useAppSelector(
    (state) => state.Evl0110m.rawBackendData,
  );
  const activeTab = useAppSelector((state) => state.Evl0110m.activeTab);

  // 화면 진입 시 기본값 강제 초기화 (탭: 내평가, 필터: 전체)
  useEffect(() => {
    dispatch(Evl0110mAction.setActiveTab("my"));
    dispatch(Evl0110mAction.resetFilters());
    setIsViewInitialized(true);
  }, [dispatch]);

  // 초기화 완료 후 데이터 로드 및 activeTab 변경 시 재조회
  useEffect(() => {
    if (!isViewInitialized) return;
    const empNo = common.getEmpNo();
    fetchEvaluationData(empNo, activeTab);
  }, [activeTab, isViewInitialized]);

  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  // 탭 변경 핸들러 (관리자용 전체/내 평가)
  const handleTabChange = (key: string) => {
    dispatch(Evl0110mAction.resetFilters());
    dispatch(Evl0110mAction.setActiveTab(key as TabType));
  };

  // 백엔드 데이터 조회
  const fetchEvaluationData = (loginEmpNo: string, tabType?: TabType) => {
    const isHrAdminFlag = common.isHrAdmin() && tabType === "all" ? 1 : 0;

    const requestData = {
      loginEmpNo: loginEmpNo,
      evlYear: new Date().getFullYear().toString(),
      isHrAdmin: isHrAdminFlag,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0009",
      success: (responseData: any) => {
        const evaluationTargets =
          responseData.data?.EvlMemberPeriodItems ||
          responseData.EvlMemberPeriodItems ||
          [];

        if (evaluationTargets.length > 0) {
          const convertedData: IEvlMemberListState[] = evaluationTargets.map(
            (target: any, index: number) => {
              let deadlineBackend = "";
              if (target.evlType === "BIZ") {
                if (target.evlLvl === 0) {
                  deadlineBackend = target.bizPlgEdDt || "";
                }
              } else if (target.evlType === "PJ") {
                deadlineBackend = target.pjtEvlEdDt || "";
              } else if (target.evlType === "HR") {
                if (target.evlLvl === 1) deadlineBackend = target.slfEdDt || "";
                else if (target.evlLvl === 2)
                  deadlineBackend = target.eval1EdDt || "";
                else if (target.evlLvl === 3)
                  deadlineBackend = target.eval2EdDt || "";
              }
              const deadline = formatDateFromBackend(deadlineBackend);

              let submitYn = "N";
              if (target.evlType === "BIZ") {
                submitYn = target.bizPlgSubYn || "N";
              } else if (target.evlType === "PJ") {
                submitYn = target.pjtSubYn || "N";
              } else if (target.evlType === "HR") {
                if (target.evlLvl === 1) submitYn = target.slfSubYn || "N";
                else if (target.evlLvl === 2)
                  submitYn = target.eval1SubYn || "N";
                else if (target.evlLvl === 3)
                  submitYn = target.eval2SubYn || "N";
              }

              return {
                id: `${target.empNo}-${target.evlType}-${target.evlLvl}-${index}`,
                empNo: target.empNo,
                name: target.empNm,
                team: target.dptNm,
                position: target.posNm,
                eval1EmpNo: target.eval1EmpNo,
                eval1EmpNm: target.eval1EmpNm,
                eval1PosNm: target.eval1PosNm,
                eval2EmpNo: target.eval2EmpNo,
                eval2EmpNm: target.eval2EmpNm,
                eval2PosNm: target.eval2PosNm,
                evlLvl: target.evlLvl,
                evlType: target.evlType,
                pmEmpNo: target.pmEmpNo,
                pmEmpNm: target.pmEmpNm,
                pmPosNm: target.pmPosNm,
                pjtEvlCd: target.pjtEvlCd,
                projectCd: target.projectCd,
                projectNm: target.projectNm,
                deadline: deadline,
                status: convertStatus(submitYn, deadlineBackend),
                isEvaluatorGroup: false,
                eval1CommonEdDt: target.eval1CommonEdDt,
                eval2CommonEdDt: target.eval2CommonEdDt,
                evlYear: target.evlYear,
                eval1SubYn: target.eval1SubYn,
                eval2SubYn: target.eval2SubYn,
              };
            },
          );

          dispatch(Evl0110mAction.setAllEmployees(convertedData));
          dispatch(Evl0110mAction.setRawBackendData(evaluationTargets));
        } else {
          message.info("평가 대상자가 없습니다.");
          dispatch(Evl0110mAction.setAllEmployees([]));
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || "평가 대상자 조회에 실패했습니다.");
      },
    });
  };

  // 날짜 포맷 변환 (YYYYMMDD -> YYYY-MM-DD)
  const formatDateFromBackend = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) return "";
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  };

  const convertStatus = (
    subYn: string,
    deadlineBackend: string,
  ): EvaluationStatus => {
    if (subYn === "Y") return "completed";
    if (!deadlineBackend || deadlineBackend.length !== 8) return "not-started";
    const today = dayjs().format("YYYYMMDD");
    if (deadlineBackend < today) return "overdue";
    return "not-started";
  };

  const getEvaluationTypeName = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") return "업무서약서";
    if (employee.evlType === "PJ") {
      if (employee.pjtEvlCd === "03") return "프로젝트평가-PM";
      if (employee.pjtEvlCd === "04") return "프로젝트평가-팀원";
      return "프로젝트평가";
    }
    if (employee.evlLvl === 1) return "자기평가";
    if (employee.evlLvl === 2) return "1차평가";
    if (employee.evlLvl === 3) return "2차평가";
    return "자기평가";
  };

  const getEvaluatorName = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") return "-";
    if (employee.evlType === "PJ") return employee.pmEmpNm || "-";
    if (employee.evlType === "HR") {
      if (employee.evlLvl === 1) return "-";
      if (employee.evlLvl === 2) return employee.eval1EmpNm || "-";
      if (employee.evlLvl === 3) return employee.eval2EmpNm || "-";
    }
    return "-";
  };

  const getEvaluatorPosition = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") return "-";
    if (employee.evlType === "PJ") return employee.pmPosNm || "-";
    if (employee.evlType === "HR") {
      if (employee.evlLvl === 1) return "-";
      if (employee.evlLvl === 2) return employee.eval1PosNm || "-";
      if (employee.evlLvl === 3) return employee.eval2PosNm || "-";
    }
    return "-";
  };

  const getStatusTag = (status: string) => {
    const statusMap: any = {
      completed: { color: "success", text: "완료" },
      "in-progress": { color: "processing", text: "진행중" },
      overdue: { color: "error", text: "기한초과" },
      "not-started": { color: "default", text: "미작성" },
    };
    const config = statusMap[status] || statusMap["not-started"];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleExtendDeadline = (record: IEvlMemberListState) => {
    if (record.evlLvl === 0 || record.evlLvl === 1 || record.evlLvl === 4) {
      if (record.eval1SubYn === "Y") {
        Modal.warning({
          title: "기한 연장 불가",
          content: "1차평가가 이미 제출되어 기한을 연장할 수 없습니다.",
          okText: "확인",
        });
        return;
      }
    }

    if (record.evlLvl === 2) {
      if (record.eval2SubYn === "Y") {
        Modal.warning({
          title: "기한 연장 불가",
          content: "2차평가가 이미 제출되어 기한을 연장할 수 없습니다.",
          okText: "확인",
        });
        return;
      }
    }

    setSelectedRecord(record);
    setSelectedDate(null);
    setIsExtendModalOpen(true);
  };

  const handleChangeStatus = (record: IEvlMemberListState) => {
    if (record.evlLvl === 0 || record.evlLvl === 1 || record.evlLvl === 4) {
      if (record.eval1SubYn === "Y") {
        Modal.warning({
          title: "상태 변경 불가",
          content: "1차평가가 이미 제출되어 상태를 변경할 수 없습니다.",
          okText: "확인",
        });
        return;
      }
    }

    if (record.evlLvl === 2) {
      if (record.eval2SubYn === "Y") {
        Modal.warning({
          title: "상태 변경 불가",
          content: "2차평가가 이미 제출되어 상태를 변경할 수 없습니다.",
          okText: "확인",
        });
        return;
      }
    }

    const loginEmpNo = common.getEmpNo();
    const isDeadlinePassed =
      record.deadline && dayjs(record.deadline).isBefore(dayjs(), "day");
    if (record.status !== "completed" || isDeadlinePassed) {
      message.warning("완료 상태이고 기한 내인 평가만 상태 변경이 가능합니다.");
      return;
    }

    openConfirmModal({
      title: "상태 변경",
      content: `${record.name}님의 평가 상태를 미완료로 변경하시겠습니까?`,
      okText: "변경",
      cancelText: "취소",
      onOk: () => {
        let txCd = "";
        if (record.evlType === "BIZ") txCd = "TREVLS0021";
        else if (record.evlType === "HR") txCd = "TREVLS0019";
        else if (record.evlType === "PJ") txCd = "TREVLS0020";

        const requestData = {
          loginEmpNo: loginEmpNo,
          empNo: record.empNo,
          evlYear: new Date().getFullYear().toString(),
          evlType: record.evlType,
          evlLvl: record.evlLvl,
          submitYn: "N",
          projectCd: record.projectCd,
        };

        return new Promise((resolve, reject) => {
          commonProxy.callAsyncSvc(requestData, {
            txCd: txCd,
            success : (responseData: any) => {
                        // 백엔드 응답의 status 체크
                          const data = responseData.data || responseData;
                          const resultCode = data.resultCode;
                          
                          if (resultCode === "400") {
                          message.error('상위평가가 제출되어 상태 변경이 불가능합니다.')  ;
                          fetchEvaluationData(loginEmpNo, activeTab); // 에러일 때 재조회
                          return;
                        }
                        
                        // SUCCESS인 경우
                        message.success('평가 상태가 미완료로 변경되었습니다.');
                        fetchEvaluationData(loginEmpNo, activeTab);
                      },
            error: (errorMsg: string) => {
              message.error(errorMsg || "상태 변경에 실패했습니다.");
              reject();
            },
          });
        });
      },
    });
  };

  // 필터링된 데이터
  const displayedEmployees = useMemo(() => {
    let result = allEmployees;

    if (selectedEvalType !== "all") {
      result = result.filter((emp) => {
        const typeName = getEvaluationTypeName(emp);
        return typeName === selectedEvalType;
      });
    }

    if (selectedStatus !== "all") {
      result = result.filter((emp) => emp.status === selectedStatus);
    }

    if (selectedTeam !== "all") {
      result = result.filter((emp) => emp.team === selectedTeam);
    }

    if (searchName) {
      result = result.filter((emp) => emp.name.includes(searchName));
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    result = [...result].sort((a, b) => {
      const dateA = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      const dateB = b.deadline ? new Date(b.deadline).getTime() : Infinity;
      const timeA = isNaN(dateA) ? Infinity : dateA;
      const timeB = isNaN(dateB) ? Infinity : dateB;
      const diffA = Math.abs(timeA - todayTime);
      const diffB = Math.abs(timeB - todayTime);
      if (diffA === diffB) {
        return a.name.localeCompare(b.name, "ko-KR");
      }
      return diffA - diffB;
    });

    return result;
  }, [
    allEmployees,
    selectedEvalType,
    selectedStatus,
    selectedTeam,
    searchName,
  ]);

  const teamOptions = useMemo(() => {
    const teams = Array.from(
      new Set(allEmployees.map((emp) => emp.team)),
    ).filter(Boolean);
    const sortedTeams = teams.sort((a, b) => a.localeCompare(b, "ko-KR"));
    return [
      { label: "전체", value: "all" },
      ...sortedTeams.map((team) => ({ label: team, value: team })),
    ];
  }, [allEmployees]);

  const handleResetFilters = () => {
    dispatch(Evl0110mAction.setSelectedEvalType("all"));
    dispatch(Evl0110mAction.setSelectedStatus("all"));
    dispatch(Evl0110mAction.setSelectedTeam("all"));
    dispatch(Evl0110mAction.setSearchName(""));
    message.info("필터가 초기화되었습니다.");
  };

  const getMaxExtendDate = (
    record: IEvlMemberListState | null,
  ): dayjs.Dayjs | null => {
    if (!record) return null;

    if (record.evlType === "BIZ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, "YYYYMMDD");
        return eval1Date.subtract(1, "day");
      }

      const year = record.evlYear || new Date().getFullYear().toString();
      return dayjs(`${year}-09-30`);
    }

    if (record.evlType === "HR" && record.evlLvl === 1) {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, "YYYYMMDD");
        return eval1Date.subtract(1, "day");
      }
      return null;
    }

    if (record.evlType === "PJ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, "YYYYMMDD");
        return eval1Date.subtract(1, "day");
      }
      return null;
    }

    if (record.evlType === "HR" && record.evlLvl === 2) {
      if (record.eval2CommonEdDt && record.eval2CommonEdDt.length === 8) {
        const eval2Date = dayjs(record.eval2CommonEdDt, "YYYYMMDD");
        return eval2Date.subtract(1, "day");
      }
      return null;
    }

    if (record.evlType === "HR" && record.evlLvl === 3) {
      const year = record.evlYear || new Date().getFullYear().toString();
      return dayjs(`${year}-12-17`);
    }

    return null;
  };

  const getExtendLimitMessage = (
    record: IEvlMemberListState | null,
  ): string => {
    if (!record) return "";

    const maxDate = getMaxExtendDate(record);
    if (!maxDate) return "";

    const formattedDate = maxDate.format("YYYY-MM-DD");

    if (record.evlType === "BIZ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        return `업무서약서는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      }
      return `업무서약서는 3사분기 전(${formattedDate})까지만 연장 가능합니다.`;
    }

    if (record.evlType === "PJ") {
      return `프로젝트평가는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
    }

    if (record.evlType === "HR") {
      if (record.evlLvl === 1) {
        return `자기평가는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      }
      if (record.evlLvl === 2) {
        return `1차평가는 2차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      }
      if (record.evlLvl === 3) {
        return `2차평가는 당해년도 종료 14일 전(${formattedDate})까지만 연장 가능합니다.`;
      }
    }

    return `${formattedDate}까지 연장 가능합니다.`;
  };

  // 모바일 레이아웃 렌더링
  return (
    <div
      style={{
        padding: isMobile ? "12px" : "20px",
        marginTop: "30px",
        backgroundColor: "#f5f5f5",
        width: screenWidth - 20 + "px",
        paddingBottom: "20px",
      }}
    >
      <div style={{ textAlign: "center", marginTop: 12, marginBottom: 15 }}>
        <Text style={{ fontSize: 18, fontWeight: 700 }}>
          인사평가 마감일 관리
        </Text>
      </div>

      {/* 관리자만 모드 전환 Switch 표시 */}
      {common.isHrAdmin() && (
        <div
          style={{ marginBottom: 16, textAlign: "right", padding: "0 12px" }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 6,
              backgroundColor: activeTab === "all" ? "#e6f7ff" : "#f5f5f5",
              border:
                activeTab === "all"
                  ? "1.5px solid #1890ff"
                  : "1.5px solid #d9d9d9",
              transition: "all 0.3s ease",
            }}
          >
            <Switch
              size="small"
              checked={activeTab === "all"}
              onChange={(checked) => handleTabChange(checked ? "all" : "my")}
              style={{
                backgroundColor: activeTab === "all" ? "#1890ff" : undefined,
              }}
            />
            <Text
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: activeTab === "all" ? "#1890ff" : "#595959",
                width: 75,
                textAlign: "left",
              }}
            >
              {activeTab === "all" ? "관리자 모드" : "일반 모드"}
            </Text>
          </div>
        </div>
      )}

      {/* 필터 카드 */}
      <Card
        size="small"
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: "grid", gap: 8 }}>
          {/* 첫번째 셀 : 구분 */}
          <div>
            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              구분 :
            </div>
            <Select
              value={selectedEvalType}
              onChange={(value) =>
                dispatch(Evl0110mAction.setSelectedEvalType(value))
              }
              options={[
                { label: "전체", value: "all" },
                { label: "업무서약서", value: "업무서약서" },
                { label: "자기평가", value: "자기평가" },
                { label: "1차평가", value: "1차평가" },
                { label: "2차평가", value: "2차평가" },
                { label: "프로젝트평가-PM", value: "프로젝트평가-PM" },
                { label: "프로젝트평가-팀원", value: "프로젝트평가-팀원" },
              ]}
              style={{ width: "100%" }}
            />
          </div>

          {/* 다음 두개 : 상태 / 팀 */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#666",
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                진행상태 :
              </div>
              <Select
                value={selectedStatus}
                onChange={(value) =>
                  dispatch(Evl0110mAction.setSelectedStatus(value))
                }
                options={[
                  { label: "전체", value: "all" },
                  { label: "완료", value: "completed" },
                  { label: "기한초과", value: "overdue" },
                  { label: "미작성", value: "not-started" },
                ]}
                style={{ width: "100%" }}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#666",
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                부서 :
              </div>
              <Select
                value={selectedTeam}
                onChange={(value) =>
                  dispatch(Evl0110mAction.setSelectedTeam(value))
                }
                options={teamOptions}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              이름 :
            </div>
            <Input
              value={searchName}
              onChange={(e) =>
                dispatch(Evl0110mAction.setSearchName(e.target.value))
              }
              placeholder="이름 검색"
            />
          </div>

          <div style={{ textAlign: "right", width: "100%" }}>
            <Button onClick={handleResetFilters} size="middle">
              필터 초기화
            </Button>
          </div>
        </div>
      </Card>

      {/* 리스트: 카드 형태로 모바일 최적화 */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderRadius: 12 }}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {displayedEmployees.length === 0 && (
            <Empty description="조회된 평가 대상자가 없습니다." />
          )}
          {displayedEmployees.map((emp) => (
            <Card
              key={emp.id}
              size="small"
              style={{ borderRadius: 10, backgroundColor: "#fafafa" }}
              bodyStyle={{ padding: 12 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}
                  >
                    {emp.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                    {emp.team} • {emp.position}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Text style={{ fontSize: 12 }}>
                        {getEvaluationTypeName(emp)}
                      </Text>
                      {emp.projectNm && (
                        <Text style={{ fontSize: 12, color: "#666" }}>
                          {emp.projectNm}
                        </Text>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Text style={{ fontSize: 12 }}>
                        {emp.deadline || "-"}
                      </Text>
                      {getStatusTag(emp.status)}
                    </div>
                  </div>

                  <div style={{ fontSize: 12, color: "#444" }}>
                    평가자: {getEvaluatorName(emp)} ({getEvaluatorPosition(emp)}
                    )
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignItems: "flex-end",
                  }}
                >
                  {(() => {
                    const isDeadlinePassed =
                      emp.deadline &&
                      dayjs(emp.deadline).isBefore(dayjs(), "day");
                    const canChangeStatus =
                      emp.status === "completed" && !isDeadlinePassed;

                    return (
                      <>
                        <Button
                          type={
                            emp.status === "overdue" ? "primary" : "default"
                          }
                          size="small"
                          onClick={() => handleExtendDeadline(emp)}
                          danger={emp.status === "overdue"}
                        >
                          기한연장
                        </Button>
                        <Button
                          type="default"
                          size="small"
                          onClick={() => handleChangeStatus(emp)}
                          danger={canChangeStatus}
                          disabled={!canChangeStatus}
                        >
                          상태변경
                        </Button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>

      {/* 기한연장 모달 */}
      <Modal
        title="기한 연장"
        width={360}
        open={isExtendModalOpen}
        okText="연장"
        cancelText="취소"
        okButtonProps={{ disabled: !selectedDate }}
        onOk={() => {
          if (!selectedDate) {
            message.warning("새로운 마감일을 선택해주세요.");
            return;
          }

          const loginEmpNo = common.getEmpNo();
          const record = selectedRecord;
          if (!record) return;

          let txCd = "";
          if (record.evlType === "BIZ") txCd = "TREVLS0021";
          else if (record.evlType === "HR") txCd = "TREVLS0019";
          else if (record.evlType === "PJ") txCd = "TREVLS0020";

          const requestData = {
            loginEmpNo: loginEmpNo,
            empNo: record.empNo,
            evlYear: new Date().getFullYear().toString(),
            evlType: record.evlType,
            evlLvl: record.evlLvl,
            evlEdDt: selectedDate.format("YYYYMMDD"),
            projectCd: record.projectCd,
          };

          commonProxy.callAsyncSvc(requestData, {
            txCd: txCd,
            success: (responseData: any) => {
              const data = responseData.data || responseData;
              const resultCode = data.resultCode;
              
              if (resultCode === "400") {
                message.error('상위평가가 이미 제출 완료되어 기한을 연장할 수 없습니다.');
                fetchEvaluationData(loginEmpNo, activeTab);
                setIsExtendModalOpen(false);
                return;
              }

              message.success(
                `기한이 ${selectedDate?.format("YYYY-MM-DD")}로 연장되었습니다.`,
              );
              fetchEvaluationData(loginEmpNo, activeTab);
              setIsExtendModalOpen(false);
            },
            error: (errorMsg: string) => {
              message.error(errorMsg || "기한 연장에 실패했습니다.");
            },
          });
        }}
        onCancel={() => setIsExtendModalOpen(false)}
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ marginBottom: 8 }}>
            <strong>{selectedRecord?.name}</strong>님의 평가 기한을 연장할
            날짜를 선택하세요.
          </p>
          <DatePicker
            value={selectedDate}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            placeholder="새로운 마감 선택"
            disabledDate={(current) => {
              if (!current) return false;

              const today = dayjs().startOf("day");
              const maxDate = getMaxExtendDate(selectedRecord);

              if (current.isBefore(today)) {
                return true;
              }

              if (maxDate && current.isAfter(maxDate, "day")) {
                return true;
              }

              return false;
            }}
            onChange={(date) => {
              setSelectedDate(date);
            }}
          />
          {selectedRecord && getMaxExtendDate(selectedRecord) && (
            <div style={{ marginTop: 8, fontSize: "12px", color: "#999" }}>
              * {getExtendLimitMessage(selectedRecord)}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MobileEvl0110m;
